import React, { useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "../../components/ui/TableV2";
import Breadcrumb from "../../components/ui/Breadcrumb";
import { Badge } from "../../components/ui/Badge";
import AddCategoryModal from "../../components/modals/AddCategoryModal";
import AddSubCategoryModal from "../../components/modals/AddSubCategoryModal";
import { formatDateTime } from "src/utils/date-time";
import { formatCreatedBy } from "src/utils/common";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as categoryActions from "src/redux/slices/categorySlice";
import { toast } from "react-toastify";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import CardList from "src/components/common/card/CardList";
import { Category, SubCategory } from "src/types/category.types";

const CategoryListPage: React.FC = () => {
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [openSubCategoryModal, setOpenSubCategoryModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedParentCategory, setSelectedParentCategory] =
    useState<Category | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<SubCategory | null>(null);
  const [limit, setLimit] = useState(10);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);
  const [statusUpdate, setStatusUpdate] = useState(false);

  const { categoryList, editCategory } = useAppSelector(
    (state) => state.category
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateCategory =
    isTenantAdmin || (permissions?.products || []).includes("create");
  const canEditCategory =
    isTenantAdmin || (permissions?.products || []).includes("edit");
  const canToggleCategory =
    isTenantAdmin || (permissions?.products || []).includes("delete");
  const dispatch = useAppDispatch();

  useEffect(() => {
    getCategoryList(1);
  }, []);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editCategory.data && statusUpdate) {
      toast.success("Category modified successfully");
      dispatch(categoryActions.resetEditCategory());
      setStatusUpdate(false);
      handleStatusModalClose();
      getCategoryList(1);
    } else if (editCategory.error && statusUpdate) {
      toast.error(`Error: ${editCategory.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
  }, [editCategory, statusUpdate, dispatch]);

  const getCategoryList = (page: number) =>
    dispatch(
      categoryActions.getCategoryListRequest({
        page,
        limit,
      })
    );

  const handleCategoryModalClose = () => setOpenCategoryModal(false);
  const handleSubCategoryModalClose = () => setOpenSubCategoryModal(false);

  const handleAddCategorySuccess = () => {
    setOpenCategoryModal(false);
    setOpenSubCategoryModal(false);
    getCategoryList(1);
  };

  const handlePageChange = (page: number) => getCategoryList(page);

  const handleEdit = (data: any) => {
    const newData = {
      ...data,
      password: "",
    };
    setSelectedRow(newData);
    setSelectedParentCategory(null);
    setSelectedSubCategory(null);
    setOpenCategoryModal(true);
  };

  const handleStatusUpdate = (_id: string, status: "active" | "inactive") => {
    const payload = {
      _id: _id,
      status: status,
    };
    dispatch(categoryActions.editCategoryRequest(payload));
  };

  const handleCreateSubcategory = (category: Category) => {
    setSelectedRow(null);
    setSelectedParentCategory(category);
    setSelectedSubCategory(null);
    setOpenSubCategoryModal(true);
  };

  const handleEditSubcategory = (
    category: Category,
    subCategory: SubCategory
  ) => {
    setSelectedRow(null);
    setSelectedParentCategory(category);
    setSelectedSubCategory(subCategory);
    setOpenSubCategoryModal(true);
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    if (statusRow?.parentCategoryId) {
      dispatch(
        categoryActions.editCategoryRequest({
          _id: statusRow.parentCategoryId,
          subCategoryUpdate: {
            _id: statusRow._id,
            status: statusRow.status === "active" ? "inactive" : "active",
          },
        })
      );
      return;
    }

    handleStatusUpdate(
      statusRow._id,
      statusRow.status === "active" ? "inactive" : "active"
    );
  };

  return (
    <DashboardLayout
      meta={{
        title: "Categories | Invyntra",
        description:
          "Track and manage all product categories with detailed information.",
        keywords: "categories, products, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Categories / Sub categories"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Categories", path: "/app/categories" },
          ]}
        />

        <Card>
          <CardHeader
            title="Categories / Sub categories"
            description="Manage product categories and sub categories"
            actions={
              canCreateCategory ? (
                <Button
                  onClick={() => {
                    setSelectedRow(null);
                    setSelectedParentCategory(null);
                    setOpenCategoryModal(true);
                  }}
                >
                  <Plus size={16} className="mr-1" /> Add Category
                </Button>
              ) : null
            }
          />

          <CardList
            data={categoryList?.data?.data}
            pagination={categoryList?.data?.pagination}
            onPageChange={handlePageChange}
            titleComponent={(row) => (
              <CardTitle
                {...row}
                canEditCategory={canEditCategory}
                canToggleCategory={canToggleCategory}
                onEdit={() => handleEdit(row)}
                onToggle={() => {
                  setStatusRow(row);
                  setShowStatusModal(true);
                }}
              />
            )}
            bodyComponent={(row) => (
              <CardBody
                {...row}
                canCreateSubcategory={canCreateCategory}
                canEditSubcategory={canEditCategory}
                canToggleSubcategory={canToggleCategory}
                onCreateSubcategory={() => handleCreateSubcategory(row)}
                onEditSubcategory={(subCategory) =>
                  handleEditSubcategory(row, subCategory)
                }
                onToggleSubcategory={(subCategory) => {
                  setStatusRow({
                    ...subCategory,
                    parentCategoryId: row._id,
                  });
                  setShowStatusModal(true);
                }}
              />
            )}
          />

          {/* Table */}
          {/* <TableV2
            columns={[
              {
                key: "categoryId",
                header: "Category ID",
                accessor: ({ key, row }) => (
                  <div className="w-20">{row[key]}</div>
                ),
              },
              {
                key: "name",
                header: "Category Name",
                accessor: ({ key, row }) => (
                  <div className="w-32">{row[key]}</div>
                ),
              },
              { key: "description", header: "Description" },
              {
                key: "level",
                header: "Level",
                accessor: ({ key, row }) => (
                  <div>{row[key] === 0 ? "Main" : `Sub (${row[key]})`}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={row?.[key] === "active" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "createdAt",
                header: "Created On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) => (
                  <div className="pr-2">
                    {canEditCategory && (
                      <button
                        className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                        onClick={() => handleEdit(row)}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canToggleCategory && (
                      <button
                        className={`p-2 rounded-md ${
                          row.status === "active"
                            ? "hover:bg-red-100 text-red-600"
                            : "hover:bg-green-100 text-green-600"
                        }`}
                        onClick={() => {
                          setStatusRow(row);
                          setShowStatusModal(true);
                        }}
                      >
                        <Power size={14} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            data={categoryList?.data?.data}
            pagination={categoryList?.data?.pagination}
            onPageChange={handlePageChange}
          /> */}
        </Card>

        {/* Add Category Modal */}
        {openCategoryModal && (
          <AddCategoryModal
            open={openCategoryModal}
            category={selectedRow}
            onClose={handleCategoryModalClose}
            onSuccess={handleAddCategorySuccess}
          />
        )}

        {openSubCategoryModal && (
          <AddSubCategoryModal
            open={openSubCategoryModal}
            parentCategoryId={selectedParentCategory?._id}
            parentCategoryName={selectedParentCategory?.name}
            subCategory={selectedSubCategory}
            onClose={handleSubCategoryModalClose}
            onSuccess={handleAddCategorySuccess}
          />
        )}
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } this ${statusRow.parentCategoryId ? "subcategory" : "category"}?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editCategory.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default CategoryListPage;

const CardTitle = (
  props: Category & {
    canEditCategory?: boolean;
    canToggleCategory?: boolean;
    onEdit?: () => void;
    onToggle?: () => void;
  }
) => {
  return (
    <div className="pr-12">
      <div className="flex items-center justify-between">
        <span className="text-sm">
          Category Name: <b>{props.name}</b>
        </span>
        <div className="flex items-center gap-2">
          <Badge
            text={props.status || ""}
            color={props.status === "active" ? "green" : "red"}
          />
          {props.canEditCategory && props.onEdit && (
            <button
              className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
              onClick={props.onEdit}
            >
              <Pencil size={14} />
            </button>
          )}
          {props.canToggleCategory && props.onToggle && (
            <button
              className={`p-2 rounded-md ${
                props.status === "active"
                  ? "hover:bg-red-100 text-red-600"
                  : "hover:bg-green-100 text-green-600"
              }`}
              onClick={props.onToggle}
            >
              <Power size={14} />
            </button>
          )}
        </div>
      </div>
      <div>
        <span className="text-xs text-gray-500 underline">
          #{props.categoryId}
        </span>
      </div>
    </div>
  );
};

const CardBody = (
  props: Category & {
    canCreateSubcategory?: boolean;
    canEditSubcategory?: boolean;
    canToggleSubcategory?: boolean;
    onCreateSubcategory?: () => void;
    onEditSubcategory?: (subCategory: SubCategory) => void;
    onToggleSubcategory?: (subCategory: SubCategory) => void;
  }
) => {
  const subcategories = props.subCategories || props.subcategories || [];
  return (
    <div className="">
      <div>
        <label className="text-xs text-gray-500">Description:</label>
        <p className="text-sm">{props.description}</p>
      </div>
      <p className="text-sm">
        <span className="text-xs text-gray-500">Created At:</span>{" "}
        {formatDateTime(props.createdAt)}
      </p>
      <p className="text-sm">
        <span className="text-xs text-gray-500">Created By:</span>{" "}
        {formatCreatedBy(props.createdBy)}
      </p>

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase">Subcategories</span>
        </div>

        <TableV2
          columns={[
            { key: "name", header: "Name" },
            { key: "description", header: "Description" },
            {
              key: "status",
              header: "Status",
              accessor: ({ key, row }) => (
                <Badge
                  text={row?.[key] || ""}
                  color={row?.[key] === "active" ? "green" : "red"}
                />
              ),
            },
            {
              key: "createdAt",
              header: "Created On",
              accessor: ({ key, row }) => (
                <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
              ),
            },
            {
              key: "actions",
              header: "Actions",
              accessor: ({ row }) => (
                <div className="flex items-center gap-1">
                  {props.canEditSubcategory && props.onEditSubcategory && (
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() => props.onEditSubcategory?.(row)}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {props.canToggleSubcategory && props.onToggleSubcategory && (
                    <button
                      className={`p-2 rounded-md ${
                        row.status === "active"
                          ? "hover:bg-red-100 text-red-600"
                          : "hover:bg-green-100 text-green-600"
                      }`}
                      onClick={() => props.onToggleSubcategory?.(row)}
                    >
                      <Power size={14} />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          data={subcategories}
          className="bg-white rounded-lg"
        />

        {props.canCreateSubcategory && props.onCreateSubcategory && (
          <div className="pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={props.onCreateSubcategory}
            >
              <Plus size={14} className="mr-1" /> Create Subcategory
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
